import React from "react";
import Box from "@mui/material/Box/Box";
import GenieText from "../../../../components/core/genie-text/GenieText";
import { useNavigate, useParams } from "react-router-dom";
import GenieStepper from "../../../../components/core/genie-stepper/GenieStepper";
import { Product } from "../../../../types/api/Product";
import { formatCurrency } from "../../../../utils/currency";
import useCurrentCart from "../../../../queries/routes/useCurrentCart";
import useUser from "../../../../queries/routes/useUser";
import useDeleteLineItem from "../../../../queries/routes/useDeleteLineItem";
import useAddLineItem from "../../../../queries/routes/useAddLineItem";
import Chip from "@mui/material/Chip";
import { useMediaQuery } from "@mui/material";
import { theme } from "../../../../components/special/genie-theme/genie-theme";

export const PRODUCT_TILE_WIDTH = 250;
export const PRODUCT_TILE_HEIGHT = 250;

interface Props {
  product: Product;
}

function ProductTile({ product }: Props) {
  const navigate = useNavigate();
  const { org_id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data: user } = useUser();

  const { data: cart, isLoading: isCartLoading } = useCurrentCart(
    org_id!,
    user?.organizations[0].id!
  );
  const currentLineItem = cart?.line_items.find((lineItem) => {
    return lineItem.product.id === product.id;
  });

  const { mutate: addLineItem, isLoading: isAddLineItemLoading } =
    useAddLineItem(cart?.id || "", org_id!, user?.organizations[0].id!);
  const { mutate: deleteLineItem, isLoading: isDeleteLineItemLoading } =
    useDeleteLineItem(
      currentLineItem?.id || "",
      org_id!,
      user?.organizations[0].id!
    );

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-block",
        cursor: "pointer",
        padding: 1,
        transition: "all 0.2s ease-in-out",
        border: "1px solid",
        borderRadius: 1,
        borderColor: "divider",
        ":hover": {
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
        },
      }}
      onClick={() => {
        navigate(`/storefront/${org_id}/product/${product.id}`);
      }}
    >
      <Box mb={1} height={"32px"} position={"absolute"} right={16} top={16}>
        {product?.order_unit?.unit && (
          <Chip
            color={"primary"}
            variant={"outlined"}
            label={product.order_unit.unit}
          />
        )}
      </Box>
      <Box
        component={"img"}
        alt={`Product tile`}
        width={"100%"}
        sx={{ aspectRatio: "1 / 1" }}
        src={
          product.media.length > 0
            ? product.media[0].media.cloudinary_url
            : `https://source.unsplash.com/random/${PRODUCT_TILE_WIDTH}x${PRODUCT_TILE_HEIGHT}`
        }
      />
      <Box mt={1}>
        <Box height={isMobile ? 72 : 48}>
          <GenieText fontWeight={"bold"} fontSize={"small"}>
            {product.name}
          </GenieText>
        </Box>
        <Box my={1}>
          <GenieText fontSize={isMobile ? "tiny" : "small"}>
            {formatCurrency(product.unit_price_in_cents)} (MSRP:{" "}
            {formatCurrency(product.msrp_in_cents)})
          </GenieText>
        </Box>

        <GenieStepper
          loading={
            isAddLineItemLoading || isDeleteLineItemLoading || isCartLoading
          }
          quantity={currentLineItem?.quantity || 0}
          add={(quantity) => {
            if (quantity === 0) {
              deleteLineItem(currentLineItem!.id);
              return;
            }
            addLineItem({
              product_id: product.id,
              quantity: quantity
                ? quantity
                : currentLineItem
                ? currentLineItem.quantity + 1
                : 1,
            });
          }}
          remove={() => {
            currentLineItem && currentLineItem.quantity === 1
              ? deleteLineItem(currentLineItem!.id)
              : addLineItem({
                  product_id: product.id,
                  quantity: currentLineItem ? currentLineItem.quantity - 1 : 1,
                });
          }}
        />
      </Box>
    </Box>
  );
}

export default ProductTile;
